import React, {useState, useEffect, useMemo}			from 'react';
import {useNavigate}								from 'react-router-dom';
import {useReactTable, ColumnDef, getCoreRowModel, getFilteredRowModel, getFacetedMinMaxValues, getFacetedRowModel, getFacetedUniqueValues, getSortedRowModel}	from '@tanstack/react-table';
import {QRCodeSVG}									from 'qrcode.react';

import Badge										from '../ressources/components/Badge';
import Button										from '../ressources/components/Button';
import Select										from '../ressources/components/Select';
import Load											from '../ressources/components/Load/Load';
import ModalAssign									from '../ressources/components/ModalAssign';
import Icon											from '../ressources/components/Icon';
import Avatar										from '../ressources/components/Avatar';
import Table										from '../ressources/components/Table';

import {useAppSelector, useAppDispatch}				from '../app/hooks';
import {assignDevicesApi, getDevicesApi}			from '../redux/devices/devices';
import {getCompanyUsersApi}							from '../redux/companyUsers/companyUsers';
import {Device}										from '../redux/devices/interface';
import {LINES_DATA, MobileLine}									from '../redux/mobileLines/interface';

import {ft_selectDataUser}							from '../ressources/functions/selectData';
import {ft_formatUserName}							from '../ressources/functions/format';
import {ft_genNotification, ft_pushNotification}	from '../ressources/functions/notification';
import {ft_findUser}								from '../ressources/functions/userFunction';

import img_usersPlus								from '../ressources/images/icon/users-plus.svg';
import img_deviceIcon								from '../ressources/images/icon/devices/computer.svg';
import img_eye										from '../ressources/images/icon/eye-open.svg';
import img_search									from '../ressources/images/icon/search.svg';

import LANGUAGE										from '../ressources/language';

import './MobileLines.css';
import Modal											from '../ressources/components/Modal';
import Input from '../ressources/components/Input';
import {InputValues} from '../ressources/components/Input/interface';
import {FilterChecked} from '../ressources/components/Modal/interface';
import {LanguageType} from '../ressources/language/type';
import {ft_checkMobileLineFilters} from '../ressources/functions/filters';


function Devices() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const users = useAppSelector(state => state.companyUsers.data);
	const mobileLines = useAppSelector(state => state.mobileLines.data);
	const mobileLinesLoad = useAppSelector(state => state.mobileLines.isLoad);
	const lang = useAppSelector(state => state.settings.lang);
	const notificationId = useAppSelector(state => state.notifications.lastId);

	const [load, set_load] = useState(false);
	const [modalAssign, set_modalAssign] = useState<false | Device>(false);
	const [modalQr, set_modalQr] = useState<LINES_DATA | undefined>();

	const [filters, set_filters] = useState<FilterChecked[]>([]);
	const [search, set_search] = useState<InputValues>('');

	const [viewPuk, set_viewPuk] = useState<string[]>([]);

	const TEXTE = LANGUAGE(lang);
	const navigate = useNavigate();
	const statusesUniques = [...new Set(mobileLines.map(item => item.lineData?.status))];

	const useData = useMemo(() =>
		mobileLines.filter(userData => ft_checkMobileLineFilters(filters, userData))
	, [mobileLines, filters]);

	const columns = useMemo<ColumnDef<MobileLine>[]>(() => [
		{
			id:         'number',
			header:     TEXTE.mobileLines.number,
			accessorFn: row => row.phoneNumber
		},
		{
			id:         'user',
			header:     TEXTE.mobileLines.user,
			accessorFn: row => ft_formatUserName(users.find(f => f?._id === row?.user)) || '',
			cell:       (v) => {
				const device = v.row.original;
				const lineData = device.lineData;
				let assign = '';

				if (device.user)
					assign = typeof(device.user) === 'string' ? device.user : device.user._id;
				else if (lineData?.identity)
					assign = ft_findUser(users, `${lineData.identity.firstName} ${lineData.identity.lastName}`) || '';

				return (
					<div>
						{assign ?
							<Select
								value={assign}
								data={[
									...ft_selectDataUser(users),
									{
										value:  'identity',
										render: (
											<div>
												<Avatar
													lastname={lineData?.identity?.lastName}
													firstname={lineData?.identity?.firstName}
												/>
												<span>{`${lineData?.identity?.firstName} ${lineData?.identity?.lastName}`}</span>
											</div>
										)
									}
								]}
								onClick={() => set_modalAssign(device)}
							/>
							:
							<Badge
								color={'gray'}
								img={img_usersPlus}
								text={TEXTE.global.affect}
								onClick={() => set_modalAssign(device)}
							/>
						}
					</div>
				);
			}
		},
		{
			id:         'operator',
			header:     TEXTE.mobileLines.operator,
			accessorFn: row => row.shopData?.info.operator
		},
		{
			id:         'status',
			header:     TEXTE.global.status,
			accessorFn: row => (row.lineData ? TEXTE.mobileLines.status[row.lineData.status] : '')
		},
		{
			id:         'planType',
			header:     TEXTE.mobileLines.package,
			accessorFn: row => (row.lineData?.type ? TEXTE.mobileLines.planTypes[row.lineData.type] : '')
		},
		{
			id:         'data',
			header:     TEXTE.mobileLines.data,
			cell:   (v) => {
				const lineData = v.row.original.lineData;

				if (!lineData || ['terminated', 'suspended'].includes(lineData.status))
					return '';
				if (!lineData?.usage)
					return <Load center size={16} />;

				const convertToGo = (dataSize?: number, isUsage = false) => {
					if (typeof dataSize !== 'number' || dataSize < 0)
						return 0;
					return isUsage ? (dataSize / 1e6).toFixed(2) : (dataSize / 1000);
				};

				return `${convertToGo(lineData.usage?.count, true)}Go / ${convertToGo(lineData.phonePlans?.[0]?.threshold)}Go`;
			}
		},
		{
			id:         'simFormat',
			header:     TEXTE.mobileLines.simFormat,
			accessorFn: (row) => {
				if (!row.lineData?.simCard?.type)
					return '';
				return row.lineData.simCard.type === 'esim' ? TEXTE.mobileLines.sim.esim : TEXTE.mobileLines.sim.physical;
			},
			cell:   (v) => {
				const row = v.row.original;

				if (!row.lineData?.simCard?.type)
					return '';

				if (row.lineData.simCard.type === 'esim') {
					return (
						<>
							{TEXTE.mobileLines.sim.esim}&nbsp;&nbsp;
							<b
								className={'primary-600'}
								style={{cursor: 'pointer'}}
								onClick={() => set_modalQr(row.lineData)}
							>
								{'(QR Code)'}
							</b>
						</>
					);
				}

				return TEXTE.mobileLines.sim.physical;
			}
		},
		{
			id:         'serial',
			header:     TEXTE.global.serial,
			accessorFn:   row => row.lineData?.simCard?.serialNumber || ''
		},
		{
			id:     'puk',
			header: TEXTE.mobileLines.puk,
			cell:   (v) => {
				const lineData = v.row.original.lineData;

				if (!lineData)
					return <></>;

				if (viewPuk.some(f => f === lineData?.id))
					return lineData.simCard?.pukCode;

				return (
					<Icon
						icon={img_eye}
						onClick={() => set_viewPuk([...viewPuk, lineData.id])}
					/>
				);
			}
		}
	], [TEXTE, users, viewPuk, useData]);

	const table = useReactTable({
		columns,
		data:                   useData,
		state:                  {globalFilter: search},
		getCoreRowModel:          getCoreRowModel(),
		getFilteredRowModel:      getFilteredRowModel(),
		getSortedRowModel:        getSortedRowModel(),
		getFacetedRowModel:       getFacetedRowModel(),
		getFacetedUniqueValues:   getFacetedUniqueValues(),
		getFacetedMinMaxValues:   getFacetedMinMaxValues()
	});

	useEffect(() => {
		dispatch(getDevicesApi(user));
		dispatch(getCompanyUsersApi(user));
	}, []);

	function assignDevice(assignUser?: string) {
		if (typeof modalAssign === 'boolean' || !modalAssign?._id)
			return;

		const newUser = users.find(f => f?._id === assignUser);
		set_load(true);

		dispatch(assignDevicesApi({
			user,
			assignUser: newUser,
			devicesId:  [modalAssign._id]
		})).then(() => {
			const notificationData = ft_genNotification(TEXTE, 'assignDeviceSuccess', img_deviceIcon, ft_formatUserName(newUser));
			ft_pushNotification(dispatch, notificationId, notificationData);

			set_load(false);
			set_modalAssign(false);
		});
	}

	function renderQrCodeModal() {
		if (!modalQr)
			return;

		const renderLine = (title: string, text: string) => <p><b>{title}</b><br />{text}</p>;
		const splitCode = modalQr.simCard?.activationCode.split('$') || [];

		return (
			<Modal
				header={TEXTE.mobileLines.qr}
				onClose={() => set_modalQr(undefined)}
			>
				<div style={{display: 'flex', gap: 12, alignItems: ''}}>
					<div>
						<QRCodeSVG value={modalQr.simCard?.activationCode || ''} />
					</div>
					<div style={{flex: 1, alignSelf: 'center'}}>
						{renderLine(TEXTE.mobileLines.lineNumber, modalQr.number)}
						{renderLine(TEXTE.mobileLines.smdp, splitCode[1])}
						{renderLine(TEXTE.mobileLines.activationCode, splitCode[2])}
					</div>
				</div>
			</Modal>
		);
	}

	function renderDevicesStatusButton(statusButton: keyof LanguageType['mobileLines']['status']) {
		return (
			<Button
				fullWidth
				size={'sm'}
				onClick={() => set_filters([{key: 'status', values: [statusButton]}])}
				type={filters.some(f => f.key === 'status' && f.values.includes(statusButton)) ? 'secondary' : 'secondary-gray'}
			>
				{TEXTE.mobileLines.status[statusButton]}
				<Badge size={'sm'} color={'gray'} text={mobileLines.filter(f => f.lineData?.status === statusButton).length} />
			</Button>
		);
	}

	return (
		<div className={'globalDataContainer'}>
			<div className={'globalDataContainerItem column flex'}>
				<Button onClick={() => navigate('/order/catalog?view=sim')}>
					{TEXTE.mobileLines.add}
				</Button>

				<div className={'flex-row'} style={{gap: 12}}>
					<Button
						fullWidth
						size={'sm'}
						onClick={() => set_filters([])}
						type={filters.some(f => f.key === 'status') ? 'secondary-gray' : 'secondary'}
					>
						{TEXTE.global.all}
						<Badge size={'sm'} color={'gray'} text={mobileLines.length} />
					</Button>
					{renderDevicesStatusButton('used')}
					{statusesUniques.map(
						status => (status && status !== 'used') && renderDevicesStatusButton(status)
					)}
				</div>

				<Input
					fullWidth
					value={search}
					onChange={set_search}
					placeholder={TEXTE.devices.search}
					before={<img src={img_search} alt={TEXTE.devices.search} />}
				/>

				<Table
					data={table}
					load={mobileLinesLoad && mobileLines.length === 0}
				/>
			</div>

			{modalAssign &&
				<ModalAssign
					modalRight
					load={load}
					onConfirm={assignDevice}
					onClose={() => set_modalAssign(false)}
					actualId={typeof modalAssign.user === 'string' ? [modalAssign.user] : [modalAssign.user?._id || '']}
				/>
			}

			{renderQrCodeModal()}
		</div>
	);
}

export default Devices;
